import { inject, Injectable } from '@angular/core';
import { Changelog } from '../model/changelog/changelog';
import { SettingsService } from '../interface/settings-service';
import { ChangelogVersionFactory } from '../model/changelog/changelog-version-factory';
import { RestService } from '../engine/http/rest.service';
import {
  BehaviorSubject,
  Observable,
  PartialObserver,
  Subject,
  Subscription,
} from 'rxjs';
import { ChangelogVersion } from '../model/changelog/changelog-version';
import { NameValue } from '../tool/name-value';
import {
  COMMONS_ENVIRONMENT,
  CommonsEnvironment,
} from '../interface/environment/commons-environment.interface';

@Injectable({
  providedIn: 'root',
})
export class ChangelogService {
  private readonly env: CommonsEnvironment = inject(COMMONS_ENVIRONMENT);
  private readonly settingsService = inject(SettingsService);
  private readonly restService = inject(RestService);

  public changelog: Changelog = new Changelog(
    this.env.app.appId,
    this.env.app.appName,
    this.settingsService.getSetting('changelog'),
  );

  private changelog$$ = new BehaviorSubject<Changelog>(null);
  public changelog$: Observable<Changelog> = this.changelog$$.asObservable();

  private event$$: Subject<NameValue> = new Subject<NameValue>();
  public event$: Observable<NameValue> = this.event$$.asObservable();

  public init(forceRefresh = false): void {
    if (!this.changelog.hasVersion || forceRefresh) {
      this.loadChangelog();
    }
  }

  protected loadChangelog(): void {
    if (!this.env.changelog) {
      return;
    }
    if (!this.env.changelog.enable) {
      return;
    }
    if (!this.env.changelog.endpoint) {
      return;
    }
    this.changelog = new Changelog(
      this.env.app.appId,
      this.env.app.appName,
      this.settingsService.getSetting('changelog'),
    );
    const changelogVersions = new ChangelogVersionFactory(
      this.env.changelog.endpoint,
    );
    this.restService
      .getAll(changelogVersions, { showLoading: false })
      .subscribe({
        next: (versionsList) => {
          versionsList.forEach((v) => {
            this.changelog.addVersion(v);
          });
          this.changelog.sortVersionsByDate();
          this.changelog$$.next(this.changelog);
        },
      });
  }

  public versionDisplayed(changelogVersion: ChangelogVersion) {
    setTimeout(() => {
      this.changelog.addReadVersion(changelogVersion);
      this.settingsService.addSetting('changelog', this.changelog.versionsRead);
      this.updateAfterDisplayed(changelogVersion);
    }, 1000);
  }

  public updateAfterDisplayed(changelogVersion: ChangelogVersion) {
    changelogVersion.isNew = false;
    this.event$$.next(new NameValue('versionUpdated', changelogVersion.id));
  }

  public subscribe(observer: PartialObserver<NameValue>): Subscription {
    return this.event$$.subscribe(observer);
  }
}
